import React, {useRef, useState} from "react";
import {useFormState} from "react-final-form";
import {useDispatch, useSelector} from "react-redux";
import OptionItem from "./summary/OptionItem";
import PromoCode from "./summary/PromoCode";
import Features from "./summary/Features";
import OrderTotal from "./sections/OrderTotal";
import {setShowSummary} from "../actions/SummaryActions";
import useCalculator from "../hooks/useCalculator";
import usePrice from "../hooks/usePrice";
import {PROBLEM_SOLVING} from "../constants/PagesNumberConstant";
import useIsMobile from "../helpers/MobileHelper";
import {getDateRange} from "../helpers/DateHelper";
import dayjs from "dayjs";
import {useIsShowMap} from "../hooks/useIsShowMap";
import PaymentButton from "../../core/components/PaymentButton/PaymentButton";
import {RESET_PASSWORD_LABELS} from "../actions/PageActions";
import {FREEBIES} from "../constants/Freebies";

function SummarySidebar({handleButtonClick, paymentButtonType, existingOrder = null}) {
    const orderOptions = useSelector(state => state.orderOptions.data);
    const {limits} = orderOptions;
    const {values, submitting} = useFormState();
    const isShowMap = useIsShowMap(values);
    const showSummary = useSelector(state => state.summary.status);
    let pageForm = useSelector(state => state.page.page);
    let profileParams = useSelector(state => state.profileParams);
    const resetPasswordStep = useSelector(state => state.page.resetPasswordStep);

    const submitResetPasswordForm = (e) => {
        e.preventDefault();
        const form = document.getElementById('reset-password-active-form');
        if (form) {
            form.requestSubmit ? form.requestSubmit() : form.dispatchEvent(new Event('submit', {bubbles: true, cancelable: true}));
        }
    };
    const dispatch = useDispatch();
    const {priceFormat} = usePrice();
    const {calculateWriterPricePerPage} = useCalculator();
    const isMobile = useIsMobile();
    const [showOrderSummary, setShowOrderSummary] = useState(false);

    const touchStartY = useRef(0);
    const touchEndY = useRef(0);

    const handleShowOrderSummary = () => {
        setShowOrderSummary(!showOrderSummary);
    };

    const getAuthorCategoryLabel = () => {
        let label = '';
        const lvl = orderOptions.writerLevel;
        Object.values(lvl).forEach(opt => {
            if (opt.value === (values.authorCategory || values.freeAuthorCategory)) {
                label = opt.label;
            }
        });
        return label;
    };

    const getAuthorCategoryPrice = () => {
        const item = Object.values(orderOptions.writerLevel).find(opt => opt.value === values.authorCategory);
        const price = calculateWriterPricePerPage(item);
        return price
            ? ` (+${priceFormat(price)} per page)`
            : ' (Free)';
    };

    const isShowPaperFormat = isShowMap.paperFormat;
    const isShowPage = isShowMap.page && values.page > 0;
    const isShowSlide = isShowMap.slide && values.slide > 0;
    const isShowQuestion = isShowMap.question && values.question > 0;
    const isShowProblem = isShowMap.problem && values.problem > 0;
    const isShowVisualPiece = isShowMap.visualPiece && values.visualPiece > 0;

    const getSeconds = hours => (hours * 60) * 60;

    const updateDeadlineResultString = () => {
        const hours = parseInt(values.exactDeadline / 3600, 10);
        const days = parseInt(values.exactDeadline / (3600 * 24), 10);
        const {dateTo} = getDateRange(values.exactDeadline);
        let result = hours > 24 ? `${days} days` : `${hours} hours`;
        result += dayjs(dateTo).format(' / ddd, MMM D');
        return result;
    };

    return (
        <aside className={`right-sidebar ${showSummary ? 'active' : ''} ${pageForm === 1 ? 'step2' : ''}`}>
            <div className="right-sidebar-sticky">
                {(showSummary || !isMobile) && (
                    <div className="sidebar-info">
                        <div className="sidebar-info__top">
                            <div className="sidebar-info__top-title">Free features</div>
                        </div>
                        <div className="sidebar-info-item">
                            <ul className="sidebar-lists">
                                {FREEBIES.map(feature => (
                                    <li key={feature.label}>
                                        <div className="sidebar-list-left">{feature.label}</div>
                                        <div className="sidebar-list-right">
                                            <span className="sidebar-list-right_price">{priceFormat(feature.price)}</span>
                                            <span className="sidebar-list-right_text">FREE</span>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="sidebar-info__top">
                            <div className="sidebar-info__top-title">Order summary</div>
                        </div>
                        <div className={`sidebar-info-item sidebar-info-item-last ${showOrderSummary ? 'active' : ''}`}>
                            <ul className={`sidebar-lists sidebar-lists-os ${showOrderSummary ? 'active' : ''}`}>
                                <OptionItem
                                    name="Academic level"
                                    value={values.academicLevel}
                                    options={orderOptions.academicLevel}
                                />
                                <OptionItem
                                    name="Type of paper"
                                    value={values.typeOfPaper}
                                    valueOther={values.typeOfPaperOther}
                                    options={orderOptions.typeOfPaper}
                                />
                                <OptionItem
                                    name="Discipline"
                                    value={values.subject}
                                    valueOther={values.subjectOther}
                                    options={orderOptions.subject}
                                />

                                {isShowPaperFormat && (
                                    <OptionItem
                                    name="Paper format"
                                    value={values.paperFormat}
                                    valueOther={values.paperFormatOther}
                                    options={orderOptions.paperFormat}
                                    />
                                )}

                                {(isMobile || showOrderSummary) && (
                                    <React.Fragment>
                                        <li>
                                            <div className="sidebar-list-left">Deadline</div>
                                            <div className="sidebar-list-right">{updateDeadlineResultString()}</div>
                                        </li>
                                        <li>
                                            <div className="sidebar-list-left">Topic</div>
                                            <div className="sidebar-list-right">
                                                {values.topic
                                                    ? values.topic.substring(0, 20) + (values.topic.length > 20 ? '…' : '')
                                                    : ''}
                                            </div>
                                        </li>
                                        {isShowPage && (
                                            <li>
                                                <div className="sidebar-list-left">{limits.page.label}</div>
                                                <div className="sidebar-list-right">
                                                    {values.page} {limits.page.label.toLowerCase()}{values.page > 1 ? 's' : ''}
                                                </div>
                                            </li>
                                        )}
                                        {values.typeOfPaper !== PROBLEM_SOLVING &&
                                            String(values.exactDeadline) > String(getSeconds(3)) &&
                                            isShowSlide && (
                                                <li>
                                                    <div className="sidebar-list-left">{limits.slide.label}</div>
                                                    <div className="sidebar-list-right">
                                                        {values.slide} {limits.slide.label.toLowerCase()}{values.slide > 1 ? 's' : ''}
                                                    </div>
                                                </li>
                                            )}
                                        {isShowQuestion && (
                                            <li>
                                                <div className="sidebar-list-left">{limits.question.label}</div>
                                                <div className="sidebar-list-right">
                                                    {values.question} question{values.question !== 1 ? 's' : ''}
                                                </div>
                                            </li>
                                        )}
                                        {isShowProblem && (
                                            <li>
                                                <div className="sidebar-list-left">{limits.problem.label}</div>
                                                <div className="sidebar-list-right">
                                                    {values.problem} problem{values.problem !== 1 ? 's' : ''}
                                                </div>
                                            </li>
                                        )}
                                        {isShowVisualPiece && (
                                            <li>
                                                <div className="sidebar-list-left">{limits.visualPiece.label}</div>
                                                <div className="sidebar-list-right">
                                                    {values.visualPiece} visual piece{values.visualPiece !== 1 ? 's' : ''}
                                                </div>
                                            </li>
                                        )}
                                        <li>
                                            <div className="sidebar-list-left">Writer level</div>
                                            <div className="sidebar-list-right">
                                                {getAuthorCategoryLabel()}{getAuthorCategoryPrice()}
                                            </div>
                                        </li>
                                        <Features values={values}/>
                                    </React.Fragment>
                                )}
                            </ul>

                            {isMobile && (pageForm === 2 || profileParams) && <PromoCode/>}
                            {isMobile && (pageForm === 2 || profileParams) && <OrderTotal existingOrder={existingOrder}/>}

                            {!isMobile && (
                                <div className="sidebar-os-trigger-panel" onClick={handleShowOrderSummary}>
                                    <div className="sidebar-os-trigger-panel__icon">
                                        <img src="/_core/assets/themes/v5/assets/img/form3/caret-down.svg" alt=""/>
                                    </div>
                                    <span className="sidebar-os-trigger-panel__text">
                    {showOrderSummary ? 'Hide summary' : 'Show all summary'}
                  </span>
                                </div>
                            )}
                        </div>

                        {!isMobile && (pageForm === 2 || profileParams) && <PromoCode/>}
                        {!isMobile && (pageForm === 2 || profileParams) && <OrderTotal existingOrder={existingOrder}/>}

                        <div
                            className="sidebar-swipe--closer"
                            onTouchStart={e => touchStartY.current = e.touches[0].clientY}
                            onTouchEnd={e => {
                                touchEndY.current = e.changedTouches[0].clientY;
                                if (touchStartY.current - touchEndY.current > 60 && showSummary) {
                                    dispatch(setShowSummary(false));
                                    document.body.classList.remove('no-scroll');
                                    document.documentElement.classList.remove('no-scroll');
                                    document.documentElement.style.overflow = '';
                                    document.body.style.overflow = '';
                                    document.documentElement.style.height = '';
                                    document.body.style.height = '';
                                }
                            }}
                        />
                    </div>
                )}

                {!isMobile && (
                    <div className="form-btn_v3">
                        {(pageForm === 2 || profileParams) ? (
                            <PaymentButton
                                type={paymentButtonType ?? 'submit'}
                                onClick={handleButtonClick}
                                className="btn_v3 btn_v3-md btn_v3-blue btn_v3-primary btn_v3-with-icon"
                                style={{width: '100%', marginTop: '24px'}}
                            >
                                Go to payment
                                <span className="btn_v3__icon">
                                    <img src="/_core/assets/themes/v5/assets/img/form3/arrow-long-right.svg" alt=""/>
                                </span>
                            </PaymentButton>
                        ) : (
                            <button
                                type={resetPasswordStep ? 'button' : (paymentButtonType ?? 'submit')}
                                onClick={resetPasswordStep ? submitResetPasswordForm : handleButtonClick}
                                className="btn_v3 btn_v3-md btn_v3-blue btn_v3-primary btn_v3-with-icon"
                                style={{width: '100%', marginTop: '24px', opacity: submitting ? 0.7 : 1, cursor: submitting ? 'wait' : 'pointer'}}
                                disabled={submitting}
                            >
                                {submitting
                                    ? 'Processing...'
                                    : (resetPasswordStep ? RESET_PASSWORD_LABELS[resetPasswordStep] : (pageForm === 1 ? 'Calculate price' : 'Next'))}
                                <span className="btn_v3__icon">
                                    <img src="/_core/assets/themes/v5/assets/img/form3/arrow-long-right.svg" alt=""/>
                                </span>
                            </button>
                        )}
                    </div>
                )}

                <div className="partner-logo-items">
                    <div className="pt-logo"><img src="/_core/assets/themes/v5/assets/img/form3/pt-logo-1.svg" alt="partner 1"/></div>
                    <div className="pt-logo"><img src="/_core/assets/themes/v5/assets/img/form3/pt-logo-2.svg" alt="partner 2"/></div>
                    <div className="pt-logo"><img src="/_core/assets/themes/v5/assets/img/form3/pt-logo-3.svg" alt="partner 3"/></div>
                </div>
            </div>
        </aside>
    );
}

export default SummarySidebar;
